import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { DateRangeInterface, DSSetup, Service } from 'webapp-types';
import { NotificationCreateType } from 'webapp/enums/notifications';

import type { Metric } from 'webapp/models-custom';
import MetricSetup from 'webapp/models-custom/metric-setup';

import type { TrackingOriginForMetricEdit } from 'webapp/services/model-manager/metrics';

export interface CreateBlockSelection {
	dimensions?: string[];
	filters?: DSSetup.MetricSetupFiltersData | null;
	dateRangeFormula?: DateRangeInterface.FormulaInterface;
}

export default abstract class MetricLibraryMetricActionsBaseComponent<T> extends Component<T> {
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/scripts') declare mmScriptsService: Service.ModelManager.Scripts;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;

	abstract get metric(): Metric | null;
	abstract get origin(): TrackingOriginForMetricEdit;

	get showDuplicateMetric() {
		const metric = this.metric;
		if (!metric || !this.canPushData || metric.isFromTokenAndNotEditable) return false;
		return true;
	}

	get isDuplicateMetricBlocked() {
		return !!this.metric?.source?.isLegacyCreationBlocked;
	}

	get canPushData() {
		return !!this.metric?.isTypeCustomPush;
	}

	get canPurgeData() {
		const metric = this.metric;
		let canPurgeCustomQuery = false;

		if (metric?.isTypeCustomQuery) {
			canPurgeCustomQuery = !!metric.canEdit;
		}

		return this.canPushData || canPurgeCustomQuery;
	}

	get canDelete() {
		const metric = this.metric;
		return metric && !metric.isTypeBasic && !metric.isPredefined && metric.canEditDefinition;
	}

	get queryModel() {
		const metric = this.metric;
		return metric?.isTypeCustomQuery ? metric.rootModel : undefined;
	}

	get scriptModel() {
		const metric = this.metric;
		return metric?.isTypeMetricCalculation ? metric.rootModel : undefined;
	}

	@action
	createBlock(selection?: CreateBlockSelection) {
		if (this.currentUserService.handleDemo()) {
			return;
		}

		const metric = this.metric;
		if (!metric) {
			return;
		}

		const metricSetup = new MetricSetup(metric.metricData!);
		if (selection?.dimensions) {
			metricSetup.dimensions = selection.dimensions;
		}
		if (selection?.filters) {
			metricSetup.setFiltersFromData(selection.filters);
		}

		this.mmBlocksService.openCreateFromMetricSetupPopup({
			trackOrigin: this.origin,
			createdNow: false,
			genericSource: metric.sourceType,
			metricSetup,
			dateRange: selection?.dateRangeFormula,
		});
	}

	// Only offered while mmMetricsService.showFavoriteAction is on; favoriting moves onto the registry in M2.
	@action
	addToFavorites(): Promise<void> {
		return Promise.resolve();
	}

	@action
	removeFromFavorites(): Promise<void> {
		return Promise.resolve();
	}

	@action
	createGoal() {
		const metric = this.metric;
		if (metric) {
			this.mmMetricsService.createGoal(metric, this.origin);
		}
	}

	@action
	createNotification() {
		const metric = this.metric;
		if (metric) {
			const metricSetup = new MetricSetup(this.metric.metricSetupData || {});
			this.mmNotificationsService.createNewForFeature(
				NotificationCreateType.METRIC,
				metricSetup,
				origin,
			);

			this.mmMetricsService.createAlert(metric, 'library');
		}
	}

	afterMetricEdit(_metrics: Metric[]) {
		// Will be overwritten
	}

	@action
	editMetric(openMetricDefinitionPopupOnInit = true) {
		const metric = this.metric;
		if (!metric) {
			return;
		}
		metric.edit(
			this.origin,
			(metrics: Metric[]) => {
				this.afterMetricEdit(metrics);
			},
			{
				openMetricDefinitionPopupOnInit,
			},
		);
	}

	@action
	editQuery() {
		this.editMetric(false);
	}

	@action
	editScript() {
		this.editMetric(false);
	}

	@action
	duplicateMetric() {
		this.mmMetricsService.duplicateMetric(this.metric);
	}

	@action
	pushData() {
		this.mmMetricsService.pushMetricData(this.metric);
	}

	@action
	purgeData() {
		this.mmMetricsService.purgeMetricData(this.metric);
	}

	@action
	deleteMetric() {
		this.mmMetricsService.deleteMetric(this.metric);
	}
}
